import {
  type ArtifactSummary,
  parseArtifactList,
} from '@modules/_shared/components/chat/artifactFiles';
import {
  chatFetch,
  chatServiceOrigin,
  getChatHeaders,
} from '@modules/_shared/components/chat/chatFetch';

// Calls to the chat API's /artifacts routes (crm-chat-api routes/artifacts.ts).
// The owner is the JWT (user + current tenant): another owner's file is a 404,
// exactly like a deleted one, and 503 means the feature is off here.

export class ArtifactRequestError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ArtifactRequestError';
    this.status = status;
  }
}

export const isArtifactNotFound = (error: unknown): boolean =>
  error instanceof ArtifactRequestError && error.status === 404;

export const isArtifactsUnavailable = (error: unknown): boolean =>
  error instanceof ArtifactRequestError && error.status === 503;

const artifactsUrl = (path: string) => `${chatServiceOrigin}/artifacts${path}`;

const request = async (
  path: string,
  init: RequestInit = {},
): Promise<unknown> => {
  const res = await chatFetch(artifactsUrl(path), {
    ...init,
    headers: getChatHeaders(),
  });
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      typeof body === 'object' &&
      body != null &&
      'error' in body &&
      typeof body.error === 'string'
        ? body.error
        : `HTTP ${res.status}`;
    throw new ArtifactRequestError(res.status, message);
  }
  return body;
};

export const listArtifacts = async (
  options: { chatId?: string; signal?: AbortSignal } = {},
): Promise<Array<ArtifactSummary>> => {
  const query =
    options.chatId != null
      ? `?${new URLSearchParams({ chatId: options.chatId }).toString()}`
      : '';
  return parseArtifactList(await request(query, { signal: options.signal }));
};

type SignedUrl = { url: string; expiresAt: string };

const parseSignedUrl = (value: unknown): SignedUrl => {
  if (
    typeof value === 'object' &&
    value != null &&
    'url' in value &&
    typeof value.url === 'string' &&
    'expiresAt' in value &&
    typeof value.expiresAt === 'string'
  ) {
    return { url: value.url, expiresAt: value.expiresAt };
  }
  throw new ArtifactRequestError(502, 'Invalid signed URL response');
};

// Margin for clock skew and the time the browser takes to start the request.
const EXPIRY_MARGIN_MS = 10_000;

const isExpired = (signed: SignedUrl, now: number): boolean =>
  Date.parse(signed.expiresAt) - EXPIRY_MARGIN_MS <= now;

// A fresh V4 URL (15 min) for each open. One that is already expired when it
// arrives (skewed clock, stalled tab) is asked for again, once.
export const signArtifactUrl = async (
  id: string,
  options: { download: boolean },
): Promise<string> => {
  const path = `/${encodeURIComponent(id)}/url${
    options.download ? '?download=1' : ''
  }`;
  const first = parseSignedUrl(await request(path));
  if (!isExpired(first, Date.now())) {
    return first.url;
  }
  return parseSignedUrl(await request(path)).url;
};

// The HTML preview reads the text through the chat API: the bucket has no
// CORS, so the signed URL can't be fetched from the CRM origin.
export const fetchArtifactText = async (
  id: string,
): Promise<{ text: string; truncated: boolean }> => {
  const body = await request(`/${encodeURIComponent(id)}/text`);
  if (
    typeof body === 'object' &&
    body != null &&
    'text' in body &&
    typeof body.text === 'string'
  ) {
    return {
      text: body.text,
      truncated: 'truncated' in body && body.truncated === true,
    };
  }
  throw new ArtifactRequestError(502, 'Invalid file content response');
};

export const deleteArtifact = async (id: string): Promise<void> => {
  await request(`/${encodeURIComponent(id)}`, { method: 'DELETE' });
};

// Starts the download without leaving the CRM: the signed URL answers with
// Content-Disposition: attachment, so the anchor never navigates.
export const downloadArtifact = async (id: string): Promise<void> => {
  const url = await signArtifactUrl(id, { download: true });
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.rel = 'noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
};

// PDFs open in a new tab from the signed URL (inline). The tab is opened
// before the await, inside the click, or popup blockers refuse it; it is cut
// from the CRM (opener = null) before it leaves about:blank.
export const openArtifactInNewTab = async (id: string): Promise<void> => {
  const tab = window.open('about:blank', '_blank');
  try {
    const url = await signArtifactUrl(id, { download: false });
    if (tab == null) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    tab.opener = null;
    tab.location.href = url;
  } catch (error) {
    tab?.close();
    throw error;
  }
};
